import { useEffect, useRef, useState } from 'react'

import { SearchIcon } from '@/assets/svg'
import { env } from '@/config/env'
import { useJobSuggestions } from '@/hooks/useJobSuggestions'
import { useLocations } from '@/hooks/useLocations'

/**
 * The hero search — legacy's `SearchJob`: a term box with title typeahead
 * (`GET /public/jobs/suggest`), a location dropdown (`GET /public/locations`), and a submit
 * that hands off to the talent app's job search. Both helpers ride talent-bff's session-less
 * `/api/public/*` (the only unauthenticated routes on the platform — see talent-bff/public.ts);
 * either failing degrades to the plain box, which is what shipped while they were absent.
 *
 * Hand-off contract: `term`/`location`/`selectedTerm` as legacy sent them, plus `cities=<elId>` —
 * the parameter the NEW talent JobSearchPage actually reads for its city filter (it never read
 * `location`, so a picked city would otherwise be dropped on arrival).
 */
export function SearchJob() {
  const [searchTerm, setSearchTerm] = useState('')
  const [locationId, setLocationId] = useState('')
  const [open, setOpen] = useState(false)
  const { titles } = useJobSuggestions(searchTerm)
  const locations = useLocations()
  const boxRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function close(event: MouseEvent) {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  function executeSearch(suggestedClicked: boolean, text?: string) {
    const term = (text ?? searchTerm).trim().toLowerCase()
    if (!term) return
    const params = new URLSearchParams({ term, location: locationId, selectedTerm: String(suggestedClicked) })
    if (locationId) params.set('cities', locationId)
    window.open(`${env.talentAppUrl}/job-search?${params.toString()}`, '_self')
  }

  const suggestions = open && searchTerm.trim().length >= 2 ? titles.slice(0, 8) : []

  return (
    <div className="search-job">
      <div className="search-job-inner container">
        <h6>
          Find <span>Your</span> Next Job
        </h6>

        <form
          className="search-job-form"
          onSubmit={(event) => {
            event.preventDefault()
            executeSearch(false)
          }}
        >
          <div className="search-job-term" ref={boxRef} style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              placeholder="Job title, keyword or company"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              aria-label="Search jobs"
              aria-autocomplete="list"
              aria-expanded={suggestions.length > 0}
              aria-controls="search-job-suggestions"
              autoComplete="off"
            />
            {suggestions.length > 0 ? (
              <ul id="search-job-suggestions" role="listbox" className="search-job-suggestions">
                {suggestions.map((title) => (
                  <li key={title} role="option" aria-selected={false}>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSearchTerm(title)
                        setOpen(false)
                        executeSearch(true, title)
                      }}
                    >
                      <img src={SearchIcon} alt="" />
                      {title}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          {locations.length > 0 ? (
            <select
              aria-label="Location"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
              className="search-job-location"
            >
              <option value="">All locations</option>
              {locations.map((location) => (
                <option key={location.elId} value={location.elId}>
                  {location.value}
                </option>
              ))}
            </select>
          ) : null}
          <button type="submit" aria-label="Search">
            <img src={SearchIcon} alt="" />
          </button>
        </form>

        <div className="search-job-bottom">
          <label>Popular Searches</label>

          <div className="search-job-tags">
            {popularSearches.map((keyword) => (
              <button
                key={keyword}
                type="button"
                className="search-tag"
                onClick={() => setSearchTerm(keyword.trim().toLowerCase())}
              >
                <img src={SearchIcon} alt="" />
                {keyword}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const popularSearches = [
  'HR Associate',
  'BI',
  'Bookkeeper',
  'Frontend Developer',
  'Data Engineer',
  'Engineering',
  'Sales',
  'Finance',
  'Customer Service',
  'Java Developer',
]
